import React from "react";

import { AddCircle } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";

interface SectionWithActionProps {
	title: string;
	count?: number;
	onAdd?: () => void;
	addButtonTitle?: string;
	emptyMessage?: string;
	children?: React.ReactNode;
	hideAddButton?: boolean;
}

/**
 * A reusable component for rendering a section with a title, optional count,
 * an optional add action button, and content or an empty state message.
 */
export default function SectionWithAction({
	title,
	count,
	onAdd,
	addButtonTitle = "Add item",
	emptyMessage = "No items found",
	children,
	hideAddButton = false,
}: SectionWithActionProps) {
	// Determine if section is empty based on children or count
	const isEmpty = !children || (count !== undefined && count === 0);

	return (
		<>
			{/* Section Header */}
			<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
					{title} {count !== undefined && `(${count})`}
				</Typography>
				{onAdd && !hideAddButton && (
					<IconButton size="small" onClick={onAdd} title={addButtonTitle} color="primary">
						<AddCircle />
					</IconButton>
				)}
			</Stack>

			{/* Section Body - Empty State or Content */}
			{isEmpty ? (
				<Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
					{emptyMessage}
				</Typography>
			) : (
				children
			)}
		</>
	);
}
